'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, CheckCircle, MapPin, Clock, Phone, User, Package, ChevronRight, Navigation, Plus, X, Upload, MessageCircle, Image, BookOpen, Calendar, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import { PAGKAIN_LIST, REGION_CITY_MAP, REGION_LIST } from '@/lib/pantryData';
import { insertSubmissionToSupabase, sendPantryEmail } from '@/lib/adminDataService';
import { createClient } from '@/lib/supabase/client';
import dynamic from 'next/dynamic';
import type { ReverseGeoResult } from './PinDropMap';

const PinDropMap = dynamic(() => import('./PinDropMap'), { ssr: false });

const FbIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877f2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const IgIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#e1306c">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

interface SubmitFormData {
  pangalan: string;
  address: string;
  barangay: string;
  lungsod: string;
  oras: string;
  contact: string;
  nagtatag: string;
  nagpadala: string;
  emailNagpadala: string;
  karagdaganImpormasyon: string;
  contactPerson: string;
  description: string;
  howItStarted: string;
  scheduleDetails: string;
}

interface AddressSuggestion {
  display_name: string;
  lat: string;
  lon: string;
}

const ARAW_OPTIONS = ['Lunes', 'Martes', 'Miyerkules', 'Huwebes', 'Biyernes', 'Sabado', 'Linggo'];

export default function SubmitPantryForm() {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [customItemInput, setCustomItemInput] = useState('');
  const [customItems, setCustomItems] = useState<string[]>([]);
  const [selectedAraw, setSelectedAraw] = useState<string[]>([]);
  const [bukasMula, setBukasMula] = useState('07:00');
  const [bukasHanggang, setBukasHanggang] = useState('11:00');
  const [scheduleType, setScheduleType] = useState<'fixed' | 'varies'>('fixed');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [pinLocation, setPinLocation] = useState<{ lat: number; lng: number } | null>(null);
  // Pantry photo
  const [pantryPhotoFile, setPantryPhotoFile] = useState<File | null>(null);
  const [pantryPhotoPreview, setPantryPhotoPreview] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const photoFileInputRef = useRef<HTMLInputElement>(null);

  // Cascading location state
  const [selectedRegion, setSelectedRegion] = useState('');
  const [availableCities, setAvailableCities] = useState<string[]>([]);

  const [facebookUrl, setFacebookUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [messengerUrl, setMessengerUrl] = useState('');
  const [nearestLandmark, setNearestLandmark] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const [emailWarning, setEmailWarning] = useState<{ type: 'pending' | 'approved'; pantryName: string } | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<SubmitFormData>();
  const addressValue = watch('address');

  useEffect(() => {
    if (!addressValue || addressValue.trim().length < 3) { setAddressSuggestions([]); setShowSuggestions(false); return; }
    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    addressDebounceRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(addressValue + ', Metro Manila, Philippines')}&format=json&limit=5&addressdetails=1`, { headers: { 'Accept-Language': 'en' } });
        const data: AddressSuggestion[] = await res.json();
        setAddressSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch { setAddressSuggestions([]); } finally { setAddressLoading(false); }
    }, 500);
    return () => { if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current); };
  }, [addressValue]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) setShowSuggestions(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleAddressSelect = (suggestion: AddressSuggestion) => {
    setValue('address', suggestion.display_name.split(',')[0]);
    setPinLocation({ lat: parseFloat(suggestion.lat), lng: parseFloat(suggestion.lon) });
    setShowSuggestions(false);
  };

  const handlePinDrop = useCallback((lat: number, lng: number, geoData?: ReverseGeoResult) => {
    setPinLocation({ lat, lng });
    if (geoData) {
      if (geoData.address) setValue('address', geoData.address, { shouldValidate: false });
      if (geoData.barangay) setValue('barangay', geoData.barangay, { shouldValidate: false });
      if (geoData.city) {
        // Try to find the region that contains this city
        for (const [region, cities] of Object.entries(REGION_CITY_MAP)) {
          const matched = cities.find((l) => l.toLowerCase().includes(geoData.city.toLowerCase()) || geoData.city.toLowerCase().includes(l.toLowerCase()));
          if (matched) {
            setSelectedRegion(region);
            setAvailableCities(cities);
            setValue('lungsod', matched, { shouldValidate: false });
            break;
          }
        }
      }
    }
  }, [setValue]);

  const checkEmailDuplicate = async (email: string) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setIsCheckingEmail(true);
    setEmailWarning(null);
    try {
      const supabase = createClient();
      // Check pending/ongoing submissions
      const { data: pendingData } = await supabase
        .from('pantry_submissions')
        .select('pangalan, sub_status')
        .ilike('email_nagpadala', email.trim())
        .in('sub_status', ['nakabinbin', 'naaprubahan'])
        .limit(1);

      if (pendingData && pendingData.length > 0) {
        const match = pendingData[0];
        setEmailWarning({
          type: match.sub_status === 'naaprubahan' ? 'approved' : 'pending',
          pantryName: match.pangalan,
        });
      }
    } catch {
      // silently ignore check errors
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const toggleItem = (item: string) => setSelectedItems((prev) => prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]);
  const addCustomItem = () => {
    const trimmed = customItemInput.trim();
    if (!trimmed) return;
    if (customItems.includes(trimmed) || PAGKAIN_LIST.includes(trimmed)) { toast.error('That item is already in the list.'); return; }
    setCustomItems((prev) => [...prev, trimmed]);
    setSelectedItems((prev) => [...prev, trimmed]);
    setCustomItemInput('');
  };
  const removeCustomItem = (item: string) => { setCustomItems((prev) => prev.filter((i) => i !== item)); setSelectedItems((prev) => prev.filter((i) => i !== item)); };
  const toggleAraw = (araw: string) => setSelectedAraw((prev) => prev.includes(araw) ? prev.filter((a) => a !== araw) : [...prev, araw]);

  const handlePantryPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Larawan lang ang pwedeng i-upload.'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Ang file ay masyadong malaki. Max 10MB lang.'); return; }
    setPantryPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPantryPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const removePantryPhoto = () => { setPantryPhotoFile(null); setPantryPhotoPreview(null); if (photoFileInputRef.current) photoFileInputRef.current.value = ''; };

  const uploadFile = async (file: File, bucket: string, prefix: string): Promise<string | null> => {
    const supabase = createClient();
    const ext = file.name.split('.').pop() ?? 'jpg';
    const fileName = `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}.${ext}`;
    const { data, error } = await supabase.storage.from(bucket).upload(fileName, file, { contentType: file.type, upsert: false });
    if (error) {
      console.error(`Upload error (bucket: ${bucket}):`, error.message, error);
      toast.error(`Upload failed: ${error.message}`);
      return null;
    }
    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(data.path);
    return urlData.publicUrl;
  };

  const onSubmit = async (data: SubmitFormData) => {
    if (selectedItems.length === 0) { toast.error('Pick at least one type of food.'); return; }
    if (scheduleType === 'fixed' && selectedAraw.length === 0) { toast.error('Pick at least one operating day.'); return; }
    if (scheduleType === 'varies' && !data.scheduleDetails?.trim()) { toast.error('Ilagay ang iyong schedule.'); return; }
    setIsSubmitting(true);
    await new Promise((r) => setTimeout(r, 800));

    let photoUrl: string | undefined;
    if (pantryPhotoFile) {
      setIsUploadingPhoto(true);
      const uploadedUrl = await uploadFile(pantryPhotoFile, 'pantry-photos', 'photo');
      setIsUploadingPhoto(false);
      if (!uploadedUrl) { toast.error('Hindi na-upload ang larawan. Subukan ulit.'); setIsSubmitting(false); return; }
      photoUrl = uploadedUrl;
    }

    // Build oras string
    let orasString: string;
    if (scheduleType === 'fixed') {
      orasString = selectedAraw.length > 0 ? `${selectedAraw.join(', ')}, ${bukasMula} - ${bukasHanggang}` : `${bukasMula} - ${bukasHanggang}`;
    } else {
      orasString = data.scheduleDetails?.trim() || 'Depende sa availability';
    }

    const result = await insertSubmissionToSupabase({
      pangalan: data.pangalan, address: data.address, barangay: data.barangay, lungsod: data.lungsod,
      lat: pinLocation?.lat ?? 14.5995, lng: pinLocation?.lng ?? 120.9842,
      items: selectedItems, oras: orasString, contact: data.contact, nagtatag: data.nagtatag,
      nagpadala: data.nagpadala, emailNagpadala: data.emailNagpadala,
      petsa: new Date().toISOString(), status: 'nakabinbin', nota: '',
      facebookUrl: facebookUrl.trim() || undefined, instagramUrl: instagramUrl.trim() || undefined,
      twitterUrl: twitterUrl.trim() || undefined, messengerUrl: messengerUrl.trim() || undefined,
      nearestLandmark: nearestLandmark.trim() || undefined, contactPerson: data.contactPerson?.trim() || undefined,
      description: data.description?.trim() || undefined,
      photoUrl,
      howItStarted: data.howItStarted?.trim() || undefined,
      scheduleType,
      scheduleDetails: scheduleType === 'varies' ? data.scheduleDetails?.trim() : undefined,
    });
    setIsSubmitting(false);
    if (!result) { toast.error('Failed to submit. Please try again.'); return; }
    setIsSuccess(true);

    sendPantryEmail('submission_received', data.emailNagpadala, {
      pantryName: data.pangalan,
      submitterName: data.nagpadala,
      submitterEmail: data.emailNagpadala,
      location: `${data.barangay}, ${data.lungsod}`,
    });

    reset();
    setSelectedItems([]); setCustomItems([]); setCustomItemInput(''); setSelectedAraw([]);
    setPinLocation(null);
    setPantryPhotoFile(null); setPantryPhotoPreview(null);
    setFacebookUrl(''); setInstagramUrl(''); setTwitterUrl(''); setMessengerUrl(''); setNearestLandmark('');
    setScheduleType('fixed');
    setSelectedRegion('');
    setAvailableCities([]);
  };

  if (isSuccess) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16 px-6">
        <div className="w-20 h-20 bg-[#dcfce7] rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={40} className="text-[#16a34a]" />
        </div>
        <h2 className="font-display text-2xl font-700 text-[#1e5c38] mb-3">Salamat sa ambag mo!</h2>
        <p className="text-[#4b5563] mb-2">Got it! Our admin team will check it out soon.</p>
        <p className="text-sm text-[#6b7280] mb-8">Once approved, it will show up on the map. A password will be sent to your email so you can manage your pantry.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => setIsSuccess(false)} className="flex items-center justify-center gap-2 px-6 py-3 bg-[#2d7a4f] hover:bg-[#1e5c38] text-white font-semibold rounded-xl transition-all duration-150 active:scale-95">Mag-submit pa</button>
          <a href="/interactive-map" className="flex items-center justify-center gap-2 px-6 py-3 border border-[#d1ead9] text-[#2d7a4f] font-semibold rounded-xl hover:bg-[#f0faf4] transition-all duration-150">Bumalik sa Mapa <ChevronRight size={16} /></a>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster position="bottom-right" richColors />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 max-w-3xl">

        {/* Section 1: Pantry Info */}
        <div className="bg-white rounded-2xl border border-[#d1ead9] overflow-hidden shadow-sm">
          <div className="px-6 py-4 bg-[#f0faf4] border-b border-[#d1ead9] flex items-center gap-3">
            <div className="w-8 h-8 bg-[#2d7a4f] rounded-lg flex items-center justify-center text-white"><MapPin size={16} /></div>
            <div><h3 className="font-display font-600 text-[#1e5c38] text-base">Impormasyon ng Pantry</h3><p className="text-xs text-[#6b7280]">Pangalan at lokasyon ng pantry</p></div>
          </div>
          <div className="px-6 py-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">Pangalan ng Pantry <span className="text-[#dc2626]">*</span></label>
              <input type="text" {...register('pangalan', { required: 'Ilagay ang pangalan ng pantry.' })} placeholder="hal. Pantry ni Aling Rosa" className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
              {errors.pangalan && <p className="mt-1 text-xs text-[#dc2626]">{errors.pangalan.message}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">Kumpletong Address <span className="text-[#dc2626]">*</span></label>
              <p className="text-xs text-[#6b7280] mb-1.5">Isama ang numero ng bahay at pangalan ng kalye.</p>
              <div ref={suggestionsRef} className="relative">
                <input type="text" {...register('address', { required: 'Ilagay ang address.' })} placeholder="hal. 42 Rizal Street, malapit sa simbahan" autoComplete="off" className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
                {addressLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2"><Loader2 size={14} className="animate-spin text-[#9ca3af]" /></div>}
                {showSuggestions && addressSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-[#d1ead9] rounded-xl shadow-lg overflow-hidden">
                    {addressSuggestions.map((s, i) => (
                      <button key={`addr-sug-${i}`} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => handleAddressSelect(s)} className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-[#f0faf4] transition-colors text-left">
                        <MapPin size={14} className="text-[#2d7a4f] mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-[#374151] line-clamp-2">{s.display_name}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {errors.address && <p className="mt-1 text-xs text-[#dc2626]">{errors.address.message}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#374151] mb-1.5 flex items-center gap-2"><Navigation size={14} className="text-[#2d7a4f]" /> I-pin ang Lokasyon sa Mapa</label>
              <p className="text-xs text-[#6b7280] mb-2">I-click ang mapa para markahan kung nasaan ang pantry.</p>
              <div className="rounded-xl overflow-hidden border border-[#d1ead9] h-64"><PinDropMap pinLocation={pinLocation} onPinDrop={handlePinDrop} /></div>
              {pinLocation && <p className="mt-1.5 text-xs text-[#2d7a4f] font-medium flex items-center gap-1"><MapPin size={12} /> Pin nalagay sa {pinLocation.lat.toFixed(5)}, {pinLocation.lng.toFixed(5)}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">Rehiyon <span className="text-[#dc2626]">*</span></label>
              <select
                value={selectedRegion}
                onChange={(e) => {
                  const region = e.target.value;
                  setSelectedRegion(region);
                  setAvailableCities(region ? REGION_CITY_MAP[region] : []);
                  setValue('lungsod', '', { shouldValidate: false });
                }}
                className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] bg-white"
              >
                <option value="">— Pumili ng Rehiyon —</option>
                {REGION_LIST.map((r) => <option key={`region-${r}`} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">Lungsod / Munisipalidad <span className="text-[#dc2626]">*</span></label>
              <select
                {...register('lungsod', { required: 'Pumili ng lungsod.' })}
                disabled={!selectedRegion}
                className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] bg-white disabled:bg-[#f3f4f6] disabled:text-[#9ca3af] disabled:cursor-not-allowed"
              >
                <option value="">{selectedRegion ? '— Pumili ng Lungsod —' : '— Pumili muna ng Rehiyon —'}</option>
                {availableCities.map((l) => <option key={`lungsod-${l}`} value={l}>{l}</option>)}
              </select>
              {errors.lungsod && <p className="mt-1 text-xs text-[#dc2626]">{errors.lungsod.message}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">Barangay <span className="text-[#dc2626]">*</span></label>
              <input type="text" {...register('barangay', { required: 'Ilagay ang barangay.' })} placeholder="hal. Barangay Pinyahan" className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
              {errors.barangay && <p className="mt-1 text-xs text-[#dc2626]">{errors.barangay.message}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">Organizer / Grupo <span className="text-[#dc2626]">*</span></label>
              <p className="text-xs text-[#6b7280] mb-1.5">Sino ang nagsimula ng pantry na ito?</p>
              <input type="text" {...register('nagtatag', { required: 'Ilagay ang pangalan ng organizer.' })} placeholder="hal. Barangay Hall / Parents Association" className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
              {errors.nagtatag && <p className="mt-1 text-xs text-[#dc2626]">{errors.nagtatag.message}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">Pangalan ng Contact Person <span className="text-[#9ca3af] font-normal">(Opsyonal)</span></label>
              <input type="text" {...register('contactPerson')} placeholder="hal. Juan dela Cruz" className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">Pinakamalapit na Landmark <span className="text-[#9ca3af] font-normal">(Opsyonal)</span></label>
              <input type="text" value={nearestLandmark} onChange={(e) => setNearestLandmark(e.target.value)} placeholder="hal. Malapit sa Jollibee, katabi ng simbahan" className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
            </div>
          </div>
        </div>

        {/* Section: Pantry Profile (NEW) */}
        <div className="bg-white rounded-2xl border border-[#d1ead9] overflow-hidden shadow-sm">
          <div className="px-6 py-4 bg-[#f0faf4] border-b border-[#d1ead9] flex items-center gap-3">
            <div className="w-8 h-8 bg-[#2d7a4f] rounded-lg flex items-center justify-center text-white"><BookOpen size={16} /></div>
            <div>
              <h3 className="font-display font-600 text-[#1e5c38] text-base">Profile ng Pantry <span className="text-[#9ca3af] font-normal text-sm">(Lahat ay Opsyonal)</span></h3>
              <p className="text-xs text-[#6b7280]">Ibahagi ang kwento ng inyong pantry</p>
            </div>
          </div>
          <div className="px-6 py-6 space-y-5">
            {/* Short description */}
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">Maikling Paglalarawan ng Pantry</label>
              <p className="text-xs text-[#6b7280] mb-1.5">Ibahagi ang kwento ng inyong pantry — paano kayo nagsimula, sino ang inyong tinutulungan, at bakit mahalaga ito sa inyong komunidad.</p>
              <textarea
                {...register('description')}
                rows={3}
                placeholder="hal. Nagsimula kami noong 2021 para tulungan ang mga pamilya sa aming barangay. Ang bawat kahon ng pagkain ay simbolo ng pagmamahal at pagkakaisa ng aming komunidad..."
                className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] resize-none"
              />
            </div>

            {/* Pantry photo */}
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5 flex items-center gap-2"><Image size={14} className="text-[#2d7a4f]" /> Larawan ng Pantry</label>
              <p className="text-xs text-[#6b7280] mb-3">Mag-upload ng litrato ng inyong pantry para makita ng mga tao.</p>
              {pantryPhotoPreview ? (
                <div className="relative inline-block">
                  <img src={pantryPhotoPreview} alt="Preview ng larawan ng pantry" className="w-full max-w-xs h-48 object-cover border-2 border-[#d1ead9] rounded-xl bg-[#f8fdf9]" />
                  <button type="button" onClick={removePantryPhoto} className="absolute -top-2 -right-2 w-6 h-6 bg-[#dc2626] text-white rounded-full flex items-center justify-center hover:bg-[#b91c1c] transition-colors shadow-sm"><X size={12} /></button>
                </div>
              ) : (
                <button type="button" onClick={() => photoFileInputRef.current?.click()} className="flex flex-col items-center justify-center gap-2 w-full max-w-xs h-36 border-2 border-dashed border-[#d1ead9] rounded-xl hover:border-[#2d7a4f] hover:bg-[#f0faf4] transition-all text-[#9ca3af] hover:text-[#2d7a4f]">
                  <Upload size={24} /><span className="text-sm font-medium">I-upload ang Larawan</span><span className="text-xs">PNG, JPG, WEBP — max 10MB</span>
                </button>
              )}
              <input ref={photoFileInputRef} type="file" accept="image/*" onChange={handlePantryPhotoChange} className="hidden" />
            </div>
          </div>
        </div>

        {/* Section 2: Available Food */}
        <div className="bg-white rounded-2xl border border-[#d1ead9] overflow-hidden shadow-sm">
          <div className="px-6 py-4 bg-[#f0faf4] border-b border-[#d1ead9] flex items-center gap-3">
            <div className="w-8 h-8 bg-[#2d7a4f] rounded-lg flex items-center justify-center text-white"><Package size={16} /></div>
            <div><h3 className="font-display font-600 text-[#1e5c38] text-base">Mga Available na Pagkain</h3><p className="text-xs text-[#6b7280]">Lagyan ng tsek lahat ng available sa pantry</p></div>
          </div>
          <div className="px-6 py-6">
            <p className="text-sm text-[#6b7280] mb-4">Piliin lahat ng available. Pwedeng i-update ng admin mamaya.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5 mb-4">
              {PAGKAIN_LIST.map((item) => {
                const isSelected = selectedItems.includes(item);
                return (
                  <button key={`pagkain-${item}`} type="button" onClick={() => toggleItem(item)} className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border transition-all duration-150 active:scale-95 text-left ${isSelected ? 'bg-[#e8f5ee] border-[#2d7a4f] text-[#1e5c38]' : 'bg-white border-[#e5e7eb] text-[#6b7280] hover:border-[#d1ead9] hover:bg-[#f8fdf9]'}`}>
                    <span className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-[#2d7a4f] border-[#2d7a4f]' : 'border-[#d1d5db]'}`}>
                      {isSelected && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </span>
                    {item}
                  </button>
                );
              })}
              {customItems.map((item) => {
                const isSelected = selectedItems.includes(item);
                return (
                  <div key={`custom-item-${item}`} className={`flex items-center gap-1 px-3 py-2.5 rounded-lg text-sm font-medium border transition-all duration-150 ${isSelected ? 'bg-[#e8f5ee] border-[#2d7a4f] text-[#1e5c38]' : 'bg-white border-[#e5e7eb] text-[#6b7280]'}`}>
                    <button type="button" onClick={() => toggleItem(item)} className="flex items-center gap-2 flex-1 text-left">
                      <span className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-[#2d7a4f] border-[#2d7a4f]' : 'border-[#d1d5db]'}`}>
                        {isSelected && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </span>
                      {item}
                    </button>
                    <button type="button" onClick={() => removeCustomItem(item)} className="text-[#9ca3af] hover:text-[#dc2626] transition-colors flex-shrink-0 ml-1" title="Remove"><X size={12} /></button>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 mt-2">
              <input type="text" value={customItemInput} onChange={(e) => setCustomItemInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomItem(); } }} placeholder="Dagdag ng sariling pagkain (hal. Lugaw, Sopas...)" className="flex-1 px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
              <button type="button" onClick={addCustomItem} className="flex items-center gap-1.5 px-4 py-2.5 bg-[#2d7a4f] hover:bg-[#1e5c38] text-white text-sm font-semibold rounded-lg transition-all active:scale-95"><Plus size={14} /> Idagdag</button>
            </div>
            {selectedItems.length > 0 && <p className="mt-3 text-xs text-[#2d7a4f] font-medium">{selectedItems.length} item(s) napili: {selectedItems.join(', ')}</p>}
          </div>
        </div>

        {/* Section 3: Operating Hours (UPDATED with schedule type) */}
        <div className="bg-white rounded-2xl border border-[#d1ead9] overflow-hidden shadow-sm">
          <div className="px-6 py-4 bg-[#f0faf4] border-b border-[#d1ead9] flex items-center gap-3">
            <div className="w-8 h-8 bg-[#2d7a4f] rounded-lg flex items-center justify-center text-white"><Clock size={16} /></div>
            <div><h3 className="font-display font-600 text-[#1e5c38] text-base">Oras ng Bukas</h3><p className="text-xs text-[#6b7280]">Kailan bukas ang pantry?</p></div>
          </div>
          <div className="px-6 py-6 space-y-5">
            {/* Schedule type selector */}
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-2">Uri ng Schedule <span className="text-[#dc2626]">*</span></label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setScheduleType('fixed')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border text-sm font-semibold transition-all ${scheduleType === 'fixed' ? 'bg-[#2d7a4f] text-white border-[#2d7a4f]' : 'bg-white text-[#6b7280] border-[#e5e7eb] hover:border-[#d1ead9]'}`}
                >
                  <Calendar size={15} />
                  Nakatakda (Fixed)
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleType('varies')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border text-sm font-semibold transition-all ${scheduleType === 'varies' ? 'bg-[#2d7a4f] text-white border-[#2d7a4f]' : 'bg-white text-[#6b7280] border-[#e5e7eb] hover:border-[#d1ead9]'}`}
                >
                  <Clock size={15} />
                  Nagbabago (Varies)
                </button>
              </div>
            </div>

            {scheduleType === 'fixed' ? (
              <>
                <div>
                  <label className="block text-sm font-semibold text-[#374151] mb-2">Mga Araw ng Bukas <span className="text-[#dc2626]">*</span></label>
                  <div className="flex flex-wrap gap-2">
                    {ARAW_OPTIONS.map((araw) => {
                      const isSelected = selectedAraw.includes(araw);
                      return (
                        <button key={`araw-${araw}`} type="button" onClick={() => toggleAraw(araw)} className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all duration-150 active:scale-95 ${isSelected ? 'bg-[#2d7a4f] border-[#2d7a4f] text-white' : 'bg-white border-[#e5e7eb] text-[#6b7280] hover:border-[#d1ead9]'}`}>{araw}</button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#374151] mb-1.5">Bukas mula</label>
                    <input type="time" value={bukasMula} onChange={(e) => setBukasMula(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#374151] mb-1.5">Hanggang</label>
                    <input type="time" value={bukasHanggang} onChange={(e) => setBukasHanggang(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
                  </div>
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-semibold text-[#374151] mb-1.5">Ilarawan ang inyong schedule <span className="text-[#dc2626]">*</span></label>
                <p className="text-xs text-[#6b7280] mb-1.5">Ilagay kung kailan kayo karaniwang bukas.</p>
                <textarea
                  {...register('scheduleDetails')}
                  rows={3}
                  placeholder="hal. Tuwing Linggo ng umaga hanggang maubos ang pagkain, o depende sa donations na natanggap"
                  className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] resize-none"
                />
              </div>
            )}
          </div>
        </div>

        {/* Section 4: Contact */}
        <div className="bg-white rounded-2xl border border-[#d1ead9] overflow-hidden shadow-sm">
          <div className="px-6 py-4 bg-[#f0faf4] border-b border-[#d1ead9] flex items-center gap-3">
            <div className="w-8 h-8 bg-[#2d7a4f] rounded-lg flex items-center justify-center text-white"><Phone size={16} /></div>
            <div><h3 className="font-display font-600 text-[#1e5c38] text-base">Contact Info</h3><p className="text-xs text-[#6b7280]">Paano maaabot ang pantry?</p></div>
          </div>
          <div className="px-6 py-6 space-y-5">
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">Contact Number <span className="text-[#dc2626]">*</span></label>
              <input type="tel" {...register('contact', { required: 'Ilagay ang contact number.', pattern: { value: /^[0-9+\-\s()]{10,15}$/, message: 'Ilagay ang tamang numero.' } })} placeholder="hal. 0917-123-4567" className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
              {errors.contact && <p className="mt-1 text-xs text-[#dc2626]">{errors.contact.message}</p>}
            </div>
            <div>
              <p className="text-sm font-semibold text-[#374151] mb-1">Social Media / Messaging Accounts <span className="text-[#9ca3af] font-normal">(Lahat ay Opsyonal)</span></p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#1877f2]/10 flex items-center justify-center flex-shrink-0"><FbIcon /></div>
                  <input type="text" value={facebookUrl} onChange={(e) => setFacebookUrl(e.target.value)} placeholder="Facebook page o profile link / username" className="flex-1 px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#e1306c]/10 flex items-center justify-center flex-shrink-0"><IgIcon /></div>
                  <input type="text" value={instagramUrl} onChange={(e) => setInstagramUrl(e.target.value)} placeholder="Instagram username o link" className="flex-1 px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-black/10 flex items-center justify-center flex-shrink-0"><XIcon /></div>
                  <input type="text" value={twitterUrl} onChange={(e) => setTwitterUrl(e.target.value)} placeholder="X / Twitter username o link" className="flex-1 px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#0084ff]/10 flex items-center justify-center flex-shrink-0"><MessageCircle size={16} className="text-[#0084ff]" /></div>
                  <input type="text" value={messengerUrl} onChange={(e) => setMessengerUrl(e.target.value)} placeholder="Messenger link o username (m.me/...)" className="flex-1 px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 5: About You */}
        <div className="bg-white rounded-2xl border border-[#d1ead9] overflow-hidden shadow-sm">
          <div className="px-6 py-4 bg-[#f0faf4] border-b border-[#d1ead9] flex items-center gap-3">
            <div className="w-8 h-8 bg-[#2d7a4f] rounded-lg flex items-center justify-center text-white"><User size={16} /></div>
            <div><h3 className="font-display font-600 text-[#1e5c38] text-base">Tungkol sa Iyo</h3><p className="text-xs text-[#6b7280]">Para makontak ka ng admin kung kailangan</p></div>
          </div>
          <div className="px-6 py-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">Pangalan Mo <span className="text-[#dc2626]">*</span></label>
              <input type="text" {...register('nagpadala', { required: 'Ilagay ang pangalan mo.' })} placeholder="Juan dela Cruz" className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f]" />
              {errors.nagpadala && <p className="mt-1 text-xs text-[#dc2626]">{errors.nagpadala.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">Email Address <span className="text-[#dc2626]">*</span></label>
              <div className="relative">
                <input
                  type="email"
                  {...register('emailNagpadala', {
                    required: 'Ilagay ang email mo.',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Ilagay ang tamang email address.' },
                  })}
                  placeholder="juan@email.com"
                  className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] pr-8"
                  onBlur={(e) => checkEmailDuplicate(e.target.value)}
                />
                {isCheckingEmail && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 size={14} className="animate-spin text-[#9ca3af]" />
                  </div>
                )}
              </div>
              {errors.emailNagpadala && <p className="mt-1 text-xs text-[#dc2626]">{errors.emailNagpadala.message}</p>}
              {emailWarning && (
                <div className={`mt-2 flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs ${emailWarning.type === 'approved' ? 'bg-[#fef3c7] border border-[#fcd34d] text-[#92400e]' : 'bg-[#fff7ed] border border-[#fed7aa] text-[#9a3412]'}`}>
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>
                    {emailWarning.type === 'approved'
                      ? <>Ang email na ito ay may <strong>naaprubahang pantry</strong> na: <strong>{emailWarning.pantryName}</strong>. Siguruhing hindi ka nag-duplicate ng registration.</>
                      : <>Ang email na ito ay may <strong>ongoing na application</strong> pa: <strong>{emailWarning.pantryName}</strong>. Kung ikaw ito, hintayin ang approval ng iyong pantry.</>
                    }
                  </span>
                </div>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">May gusto ka pang idagdag? <span className="text-[#9ca3af] font-normal">(Opsyonal)</span></label>
              <textarea {...register('karagdaganImpormasyon')} rows={3} placeholder="Anumang gusto mong malaman ng admin..." className="w-full px-3 py-2.5 text-sm border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 focus:border-[#2d7a4f] resize-none" />
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 bg-[#f0faf4] border border-[#d1ead9] rounded-xl px-5 py-4">
          <span className="text-lg flex-shrink-0">🔒</span>
          <p className="text-xs text-[#4b5563]">Ang pangalan at email mo ay para lang sa admin para sa verification at hindi ito ibabahagi sa publiko. Kapag naaprubahan ang pantry, makakatanggap ka ng password sa email para mapamahalaan ang iyong pantry.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button type="submit" disabled={isSubmitting} className="flex-1 sm:flex-none sm:w-64 flex items-center justify-center gap-2 py-3.5 px-8 bg-[#2d7a4f] hover:bg-[#1e5c38] active:scale-95 text-white font-semibold text-sm rounded-xl transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed shadow-md shadow-[#2d7a4f]/20">
            {isSubmitting ? (<><Loader2 size={16} className="animate-spin" />{isUploadingPhoto ? 'Ina-upload ang larawan...' : 'Nagsu-submit...'}</>) : (<>I-submit ang Pantry<ChevronRight size={16} /></>)}
          </button>
          <a href="/interactive-map" className="flex items-center justify-center gap-2 py-3.5 px-6 border border-[#d1ead9] text-[#4b5563] font-medium text-sm rounded-xl hover:bg-[#f0faf4] transition-all duration-150">Bumalik sa Mapa</a>
        </div>
        <p className="text-xs text-[#9ca3af]">Ang mga may <span className="text-[#dc2626]">*</span> ay kailangan.</p>
      </form>
    </>
  );
}
